import { useMemo } from 'react';
import { QueryFunction, useQuery } from '@tanstack/react-query';
import { ApiCalls } from '../../../../../api/apiv2';
import { ProjectWithStringVariants } from '../../../../../api/generated/data-contracts';
import { LocalStorage } from '../../../../../Utilities/LocalStorage';
import { fromMinutes } from '../helpers/queryHelper';
import { CURRENT_PROJECT_EXPANDED_KEY } from '../queryKeys';

export const queryCurrentProjectExpanded: QueryFunction<ProjectWithStringVariants, string[]> = async ({ queryKey: [projectWithStringVariants, projectId] }) => {
    const data = await ApiCalls.getProjectByIdExpanded({ projectId: projectId }).then(proj => proj.data);
    LocalStorage.set(CURRENT_PROJECT_EXPANDED_KEY, data);
    return data;
};

export const useCurrentProjectExpanded = (projectId?: string) => {
    // The query will not execute unless a project id is provided
    const query = useQuery({
        queryKey: [CURRENT_PROJECT_EXPANDED_KEY, projectId],
        queryFn: async context => {
            return queryCurrentProjectExpanded(context);
        },
        staleTime: fromMinutes(5),
        enabled: Boolean(projectId),
    });
    return useMemo(() => {
        if (!projectId) {
            const cachedProject = LocalStorage.get<ProjectWithStringVariants>(CURRENT_PROJECT_EXPANDED_KEY);
            // Attempt to provide the last loaded project from cache
            if (cachedProject) {
                return {
                    ...query,
                    data: cachedProject,
                };
            }
        }
        // We have exhausted all attempts to provide meaningful data, the implementation must handle fallback
        return query;
    }, [projectId, query]);
};
